<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { timeZoneOptions } from '../inbox/helpers/businessHour';

defineProps({
  errorMessage: {
    type: String,
    default: '',
  },
});

const schedule = defineModel({ type: Object, required: true });

const { t } = useI18n();

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const WEEKDAY_PRESETS = {
  WEEKDAYS: [1, 2, 3, 4, 5],
  WEEKEND: [0, 6],
  EVERY_DAY: [0, 1, 2, 3, 4, 5, 6],
};
const DAYS_OF_MONTH = [...Array.from({ length: 31 }, (_, i) => i + 1), -1];
const WEEKS_OF_MONTH = [1, 2, 3, 4, 5, -1];

const update = (key, value) => {
  schedule.value = { ...schedule.value, [key]: value };
};

const field = key =>
  computed({
    get: () => schedule.value[key],
    set: value => update(key, value),
  });

const enabled = field('enabled');
const mode = field('mode');
const timezone = field('timezone');
const frequency = field('frequency');
const interval = field('interval');
const monthlyMode = field('monthly_mode');
const weekOfMonth = field('week_of_month');
const weekday = field('weekday');
const allDay = field('all_day');
const startDate = field('start_date');
const endDate = computed({
  get: () => schedule.value.end_date || '',
  set: value => update('end_date', value || null),
});

const timezones = computed(() => {
  const options = timeZoneOptions();
  const current = schedule.value.timezone;
  if (current && !options.some(option => option.value === current)) {
    return [{ label: current, value: current }, ...options];
  }
  return options;
});

const intervalUnit = computed(
  () => `AUTOMATION.SCHEDULE.INTERVAL_UNITS.${frequency.value.toUpperCase()}`
);

const toggleItem = (key, value) => {
  const items = schedule.value[key] || [];
  const next = items.includes(value)
    ? items.filter(item => item !== value)
    : [...items, value].sort((a, b) => a - b);
  update(key, next);
};

const isSelected = (key, value) => (schedule.value[key] || []).includes(value);

const dayOfMonthLabel = day =>
  day === -1 ? t('AUTOMATION.SCHEDULE.LAST_DAY') : day;

const updateRange = (index, key, value) => {
  const ranges = schedule.value.time_ranges.map((range, i) =>
    i === index ? { ...range, [key]: value } : range
  );
  update('time_ranges', ranges);
};

const addRange = () => {
  update('time_ranges', [
    ...(schedule.value.time_ranges || []),
    { start: '08:00', end: '18:00' },
  ]);
};

const removeRange = index => {
  update(
    'time_ranges',
    schedule.value.time_ranges.filter((_, i) => i !== index)
  );
};

const isOvernight = range => range.start && range.end && range.end < range.start;

const chipClass = selected =>
  selected
    ? 'bg-n-brand text-white border-n-brand'
    : 'bg-n-solid-1 text-n-slate-12 border-n-weak hover:bg-n-alpha-2';
</script>

<template>
  <div class="pt-4 mt-4 border-t border-solid border-n-weak">
    <div class="flex items-start justify-between gap-4">
      <div>
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('AUTOMATION.SCHEDULE.TITLE') }}
        </p>
        <p class="mb-0 text-xs text-n-slate-11">
          {{ t('AUTOMATION.SCHEDULE.DESCRIPTION') }}
        </p>
      </div>
      <Switch v-model="enabled" />
    </div>

    <div v-if="enabled" class="flex flex-col gap-4 mt-4">
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label class="!mb-0">
          {{ t('AUTOMATION.SCHEDULE.MODE.LABEL') }}
          <select v-model="mode" class="!mb-0">
            <option value="inside">
              {{ t('AUTOMATION.SCHEDULE.MODE.INSIDE') }}
            </option>
            <option value="outside">
              {{ t('AUTOMATION.SCHEDULE.MODE.OUTSIDE') }}
            </option>
          </select>
        </label>
        <label class="!mb-0">
          {{ t('AUTOMATION.SCHEDULE.TIMEZONE') }}
          <select v-model="timezone" class="!mb-0">
            <option
              v-for="option in timezones"
              :key="option.label"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
      </div>

      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label class="!mb-0">
          {{ t('AUTOMATION.SCHEDULE.FREQUENCY.LABEL') }}
          <select v-model="frequency" class="!mb-0">
            <option value="daily">
              {{ t('AUTOMATION.SCHEDULE.FREQUENCY.DAILY') }}
            </option>
            <option value="weekly">
              {{ t('AUTOMATION.SCHEDULE.FREQUENCY.WEEKLY') }}
            </option>
            <option value="monthly">
              {{ t('AUTOMATION.SCHEDULE.FREQUENCY.MONTHLY') }}
            </option>
          </select>
        </label>
        <label class="!mb-0">
          {{ t('AUTOMATION.SCHEDULE.INTERVAL') }}
          <div class="flex items-center gap-2">
            <input
              v-model.number="interval"
              type="number"
              min="1"
              max="365"
              class="!mb-0 !w-24"
            />
            <span class="text-sm text-n-slate-11">{{ t(intervalUnit) }}</span>
          </div>
        </label>
      </div>

      <div v-if="frequency === 'weekly'">
        <div class="flex flex-wrap items-center justify-between gap-2 mb-2">
          <span class="text-sm text-n-slate-12">
            {{ t('AUTOMATION.SCHEDULE.DAYS_OF_WEEK') }}
          </span>
          <div class="flex gap-1">
            <NextButton
              v-for="(days, preset) in WEEKDAY_PRESETS"
              :key="preset"
              xs
              slate
              ghost
              :label="t(`AUTOMATION.SCHEDULE.PRESETS.${preset}`)"
              @click="update('days_of_week', days)"
            />
          </div>
        </div>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="(day, index) in WEEKDAYS"
            :key="day"
            type="button"
            class="px-3 py-1 text-sm border border-solid rounded-lg"
            :class="chipClass(isSelected('days_of_week', index))"
            @click="toggleItem('days_of_week', index)"
          >
            {{ t(`AUTOMATION.SCHEDULE.WEEKDAYS_SHORT.${day}`) }}
          </button>
        </div>
      </div>

      <div v-if="frequency === 'monthly'" class="flex flex-col gap-3">
        <div class="flex flex-wrap gap-4">
          <label class="flex items-center gap-2 !mb-0">
            <input
              v-model="monthlyMode"
              type="radio"
              value="day_of_month"
              class="!m-0"
            />
            {{ t('AUTOMATION.SCHEDULE.MONTHLY_MODE.DAY_OF_MONTH') }}
          </label>
          <label class="flex items-center gap-2 !mb-0">
            <input
              v-model="monthlyMode"
              type="radio"
              value="weekday_of_month"
              class="!m-0"
            />
            {{ t('AUTOMATION.SCHEDULE.MONTHLY_MODE.WEEKDAY_OF_MONTH') }}
          </label>
        </div>
        <div v-if="monthlyMode === 'day_of_month'" class="flex flex-wrap gap-1">
          <button
            v-for="day in DAYS_OF_MONTH"
            :key="day"
            type="button"
            class="h-8 px-2 text-sm border border-solid rounded-lg min-w-8"
            :class="chipClass(isSelected('days_of_month', day))"
            @click="toggleItem('days_of_month', day)"
          >
            {{ dayOfMonthLabel(day) }}
          </button>
        </div>
        <div v-else class="grid grid-cols-2 gap-3">
          <select v-model.number="weekOfMonth" class="!mb-0">
            <option v-for="week in WEEKS_OF_MONTH" :key="week" :value="week">
              {{ t(`AUTOMATION.SCHEDULE.WEEK_OF_MONTH.${week === -1 ? 'LAST' : week}`) }}
            </option>
          </select>
          <select v-model.number="weekday" class="!mb-0">
            <option v-for="(day, index) in WEEKDAYS" :key="day" :value="index">
              {{ t(`AUTOMATION.SCHEDULE.WEEKDAYS.${day}`) }}
            </option>
          </select>
        </div>
      </div>

      <div>
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm text-n-slate-12">
            {{ t('AUTOMATION.SCHEDULE.TIME_RANGES') }}
          </span>
          <label class="flex items-center gap-2 !mb-0 text-sm">
            <input v-model="allDay" type="checkbox" class="!m-0" />
            {{ t('AUTOMATION.SCHEDULE.ALL_DAY') }}
          </label>
        </div>
        <div v-if="!allDay" class="flex flex-col gap-2">
          <div
            v-for="(range, index) in schedule.time_ranges"
            :key="index"
            class="flex flex-wrap items-center gap-2"
          >
            <input
              :value="range.start"
              type="time"
              class="!mb-0 !w-32"
              @input="updateRange(index, 'start', $event.target.value)"
            />
            <span class="text-sm text-n-slate-11">
              {{ t('AUTOMATION.SCHEDULE.UNTIL') }}
            </span>
            <input
              :value="range.end"
              type="time"
              class="!mb-0 !w-32"
              @input="updateRange(index, 'end', $event.target.value)"
            />
            <span v-if="isOvernight(range)" class="text-xs text-n-slate-11">
              {{ t('AUTOMATION.SCHEDULE.NEXT_DAY') }}
            </span>
            <NextButton
              v-if="schedule.time_ranges.length > 1"
              icon="i-lucide-x"
              xs
              slate
              ghost
              @click="removeRange(index)"
            />
          </div>
          <div>
            <NextButton
              icon="i-lucide-plus"
              blue
              faded
              xs
              :label="t('AUTOMATION.SCHEDULE.ADD_TIME_RANGE')"
              @click="addRange"
            />
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label class="!mb-0">
          {{ t('AUTOMATION.SCHEDULE.START_DATE') }}
          <input v-model="startDate" type="date" class="!mb-0" />
        </label>
        <label class="!mb-0">
          {{ t('AUTOMATION.SCHEDULE.END_DATE') }}
          <input v-model="endDate" type="date" class="!mb-0" />
        </label>
      </div>

      <p v-if="errorMessage" class="mb-0 text-sm text-n-ruby-11">
        {{ errorMessage }}
      </p>
    </div>
  </div>
</template>
